import React, { useEffect, useState } from "react";
import Calling from "../../api/Calling";
import { toast } from "react-toastify";
import formatDateTime from "../../components/formatDateTime";
import generateCaptcha from "../../components/captcha";
import MainButton from "../../components/ul/MainButton";
import NotFound from "../../components/ul/NotFound";
import {
  LuBrain,
  LuCoins,
  LuExternalLink,
  LuGamepad2,
  LuPlus,
  LuSave,
  LuSquarePen,
  LuTrash2,
  LuX,
} from "react-icons/lu";

// Cash tasks: the web games and quizzes the Rewarded Play app lists on Home. The app opens the
// URL, counts the seconds the page stays open and pays `coin` for every second.
const EMPTY_FORM = { name: "", url: "", type: "game", coin: 1 };

const TYPE_TABS = [
  ["all", "All"],
  ["game", "Games"],
  ["quiz", "Quizzes"],
];

const CashTask = () => {
  const [loader, setLoader] = useState(false);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState([]);
  const [filterType, setFilterType] = useState("all");
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);

  const fetchData = async () => {
    setLoader(true);
    try {
      const resp = await Calling.GetCashTask();
      if (resp?.status) setData(resp?.data || []);
      else toast.error(resp?.message || "Failed to load cash tasks");
    } catch (error) {
      console.error(error);
      toast.error("Failed to load cash tasks");
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
  };

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || form.name.trim().length < 3) {
      return toast.error("Name must be at least 3 characters");
    }
    if (!form.url.trim()) return toast.error("URL is required");
    if (!(Number(form.coin) >= 1)) return toast.error("Coin must be at least 1");

    const formData = new FormData();
    formData.append("name", form.name.trim());
    formData.append("url", form.url.trim());
    formData.append("type", form.type);
    formData.append("coin", Number(form.coin));

    setSaving(true);
    try {
      const resp = editingId
        ? await Calling.UpdateCashTask(editingId, formData)
        : await Calling.AddCashTask(formData);
      if (resp?.status) {
        toast.success(resp?.message || "Saved");
        resetForm();
        fetchData();
      } else {
        toast.error(resp?.message || "Failed to save");
      }
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (task) => {
    setEditingId(task._id);
    setForm({
      name: task.name || "",
      url: task.url || "",
      type: task.type || "game",
      coin: task.coin ?? 1,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    if (!generateCaptcha("You are about to delete a cash task.")) return;
    setLoader(true);
    try {
      const resp = await Calling.DeleteCashTask({ id: [id] });
      if (resp?.status) {
        toast.success(resp?.message || "Deleted");
        if (editingId === id) resetForm();
        fetchData();
      } else toast.error(resp?.message || "Failed to delete");
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong");
    } finally {
      setLoader(false);
    }
  };

  const shown = filterType === "all" ? data : data.filter((t) => t.type === filterType);

  return (
    <div className="md:ml-56 px-4 sm:px-6 lg:px-8 mt-16 pb-12">
      {/* Header */}
      <div className="flex items-center gap-4 pt-6 pb-5">
        <span className="p-3.5 bg-gradient-to-br from-teal-500 to-cyan-500 text-white rounded-2xl shadow-lg shadow-teal-500/30 flex items-center justify-center">
          <LuGamepad2 className="text-2xl md:text-3xl" />
        </span>
        <div>
          <h1 className="text-2xl md:text-3xl font-extrabold text-main-font tracking-tight leading-none">
            Cash Tasks
          </h1>
          <p className="text-sm text-main-subfont mt-1.5">
            Web games and quizzes on the app's Home — paid per second of play
          </p>
        </div>
      </div>

      {/* Form */}
      <form
        onSubmit={handleSubmit}
        className="rounded-2xl bg-white border border-main-border shadow-sm p-5"
      >
        <h2 className="font-bold text-main-font">
          {editingId ? "Edit task" : "Add a task"}
        </h2>
        <p className="text-xs text-main-subfont mt-0.5">
          The app shows 3 games and 3 quizzes: the first of each type in the list below
          (lowest coin first).
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 mt-4">
          <div>
            <label className="text-[11px] uppercase tracking-wide text-main-subfont">
              Name
            </label>
            <input
              name="name"
              value={form.name}
              onChange={handleChange}
              placeholder="Task name"
              className="ui-input w-full rounded-lg px-3 py-2 text-sm mt-1"
            />
          </div>
          <div className="xl:col-span-2">
            <label className="text-[11px] uppercase tracking-wide text-main-subfont">
              URL
            </label>
            <input
              name="url"
              type="url"
              value={form.url}
              onChange={handleChange}
              placeholder="https://example.com/game"
              className="ui-input w-full rounded-lg px-3 py-2 text-sm mt-1"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-wide text-main-subfont">
                Type
              </label>
              <select
                name="type"
                value={form.type}
                onChange={handleChange}
                className="ui-input w-full rounded-lg px-3 py-2 text-sm mt-1 cursor-pointer"
              >
                <option value="game">Game</option>
                <option value="quiz">Quiz</option>
              </select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wide text-main-subfont">
                Coin / second
              </label>
              <input
                name="coin"
                type="number"
                min="1"
                value={form.coin}
                onChange={handleChange}
                className="ui-input w-full rounded-lg px-3 py-2 text-sm mt-1 text-center font-bold"
              />
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mt-4">
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-teal-500 to-cyan-500 px-5 py-2.5 text-sm font-semibold text-white shadow-md shadow-teal-500/25 hover:brightness-110 disabled:opacity-60 transition-all"
          >
            {editingId ? <LuSave size={16} /> : <LuPlus size={16} />}
            {saving ? "Saving..." : editingId ? "Save changes" : "Add task"}
          </button>
          {editingId && (
            <button
              type="button"
              onClick={resetForm}
              className="inline-flex items-center gap-2 rounded-xl border border-main-border bg-white px-5 py-2.5 text-sm font-medium text-main-subfont hover:text-main-font hover:border-brand-teal/40 transition-all"
            >
              <LuX size={16} /> Cancel
            </button>
          )}
        </div>
      </form>

      {/* Filter */}
      <div className="flex flex-wrap items-center gap-2 mt-6">
        {TYPE_TABS.map(([key, label]) => (
          <MainButton
            key={key}
            label={label}
            selectState={filterType === key}
            onClick={() => setFilterType(key)}
          />
        ))}
        <span className="text-sm text-main-subfont ml-1">
          {shown.length} task{shown.length === 1 ? "" : "s"}
        </span>
      </div>

      {/* List */}
      {loader ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 mt-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className="rounded-2xl bg-white border border-main-border shadow-sm p-5 animate-pulse"
            >
              <div className="w-32 h-4 bg-black/10 rounded" />
              <div className="w-48 h-3 bg-black/5 rounded mt-3" />
              <div className="flex gap-2 mt-5">
                <div className="flex-1 h-9 bg-black/10 rounded-lg" />
                <div className="w-9 h-9 bg-black/10 rounded-lg" />
              </div>
            </div>
          ))}
        </div>
      ) : shown.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 mt-4">
          {shown.map((task) => (
            <div
              key={task._id}
              className={`rounded-2xl bg-white border p-5 shadow-sm transition-all duration-300 ${
                editingId === task._id
                  ? "border-teal-400/50 ring-1 ring-brand-teal/30"
                  : "border-main-border hover:border-brand-teal/40 hover:shadow-md"
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="w-10 h-10 rounded-xl bg-teal-500/15 text-brand-teal flex items-center justify-center shrink-0">
                    {task.type === "quiz" ? <LuBrain size={18} /> : <LuGamepad2 size={18} />}
                  </span>
                  <div className="min-w-0">
                    <p className="font-semibold text-main-font truncate">{task.name}</p>
                    <p className="text-xs text-main-subfont capitalize">{task.type}</p>
                  </div>
                </div>
                <span className="inline-flex items-center gap-1.5 rounded-lg bg-main-bg px-2.5 py-1 text-sm font-bold text-main-font shrink-0">
                  <LuCoins size={14} className="text-brand-teal" />
                  {task.coin}/s
                </span>
              </div>

              <a
                href={task.url}
                target="_blank"
                rel="noreferrer"
                className="mt-4 flex items-center gap-2 rounded-lg bg-main-bg px-3 py-2 text-xs text-brand-teal hover:underline break-all"
              >
                <LuExternalLink size={14} className="shrink-0" />
                {task.url}
              </a>

              <p className="text-[11px] text-main-subfont mt-3">
                Added {task.createdAt ? formatDateTime(task.createdAt) : "N/A"}
              </p>

              <div className="flex gap-2 mt-4">
                <button
                  onClick={() => handleEdit(task)}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg bg-gradient-to-r from-teal-500 to-cyan-500 px-3 py-2 text-sm font-semibold text-white shadow-md shadow-teal-500/25 hover:brightness-110 transition-all"
                >
                  <LuSquarePen size={16} /> Edit
                </button>
                <button
                  onClick={() => handleDelete(task._id)}
                  title="Delete task"
                  className="inline-flex items-center justify-center rounded-lg bg-red-500/90 px-3 py-2 text-sm font-semibold text-white hover:bg-red-500 transition-all"
                >
                  <LuTrash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="mt-8">
          <NotFound title="No cash tasks" />
        </div>
      )}
    </div>
  );
};

export default CashTask;
